import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { LayoutGrid, Package, Plus } from "lucide-react";
import * as LucideIcons from "lucide-react";

type FavoriteApp = {
  id: string;
  title: string;
  slug: string;
  short_description: string;
  icon_name: string;
};

interface FavoriteAppsProps {
  basePath?: "/app" | "/paciente";
}

export function FavoriteApps({ basePath = "/app" }: FavoriteAppsProps) {
  const navigate = useNavigate();
  const [apps, setApps] = useState<FavoriteApp[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFavoriteApps();
  }, []);

  const loadFavoriteApps = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Buscar favoritos do usuário ordenados por data de criação (mais recentes primeiro)
      const { data: favorites, error: favoritesError } = await supabase
        .from('user_favorite_extensions')
        .select('extension_id, created_at')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (favoritesError) {
        console.error('Erro ao carregar favoritos:', favoritesError);
        setLoading(false);
        return;
      }

      if (!favorites || favorites.length === 0) {
        setLoading(false);
        return;
      }

      const extensionIds = favorites.map(f => f.extension_id);

      // Buscar detalhes das extensions favoritadas
      const { data: extensions } = await supabase
        .from('extensions')
        .select('id, title, slug, short_description, icon_name, is_active, is_suspended')
        .in('id', extensionIds)
        .eq('is_active', true)
        .eq('is_suspended', false);

      if (!extensions) {
        setLoading(false);
        return;
      }

      // Manter a ordem dos favoritos (mais recentes primeiro)
      const orderedApps = extensionIds
        .map(id => extensions.find(e => e.id === id))
        .filter(Boolean) as FavoriteApp[];

      setApps(orderedApps);
    } catch (error) {
      console.error('Error loading favorite apps:', error);
    } finally {
      setLoading(false);
    }
  };

  const getIcon = (iconName: string) => {
    const Icon = (LucideIcons as unknown)[iconName] || Package;
    return Icon;
  };

  // Skeleton loading
  if (loading) {
    return (
      <Card className="border-border/50 shadow-sm h-full min-h-[260px] flex flex-col">
        <CardHeader className="pb-3">
          <div className="flex items-start gap-2">
            <LayoutGrid className="h-4 w-4 text-primary mt-0.5" />
            <div className="space-y-1">
              <CardTitle className="text-sm">Meus Aplicativos</CardTitle>
              <CardDescription className="text-xs">
                Acesse rapidamente seus apps favoritos
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0 flex-1">
          <div className="flex flex-wrap items-start gap-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="inline-flex w-[86px] flex-col items-center gap-2">
                <Skeleton className="w-12 h-12 rounded-xl" />
                <Skeleton className="h-3 w-16" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-border/50 shadow-sm h-full min-h-[260px] flex flex-col">
      <CardHeader className="pb-3">
        <div className="flex items-start gap-2">
          <LayoutGrid className="h-4 w-4 text-primary mt-0.5" />
          <div className="space-y-1">
            <CardTitle className="text-sm">Meus Aplicativos</CardTitle>
            <CardDescription className="text-xs">
              Acesse rapidamente seus apps favoritos
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0 flex-1">
        <div className="flex flex-wrap items-start gap-4">
          <button
            type="button"
            onClick={() => navigate(`${basePath}/aplicativos`)}
            className="group inline-flex w-[86px] flex-col items-center gap-2"
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-primary/30 bg-primary/5 text-primary transition-colors group-hover:bg-primary/10">
              <Plus className="h-6 w-6" aria-hidden />
            </span>
            <span className="text-[13px] font-semibold leading-tight text-center text-foreground">
              Adicionar
            </span>
          </button>

          {apps.map((app) => {
            const Icon = getIcon(app.icon_name);
            return (
              <button
                type="button"
                key={app.id}
                onClick={() => navigate(`${basePath}/aplicativos/${app.slug}`)}
                className="group inline-flex w-[86px] flex-col items-center gap-2"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary transition-colors group-hover:bg-primary/15">
                  <Icon className="h-6 w-6" aria-hidden />
                </span>
                <span className="text-[13px] font-semibold leading-tight text-center text-foreground break-words">
                  {app.title}
                </span>
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

